import React, { useState, useEffect } from 'react';
import { useLanguage } from '../components/context/LanguageContext';
import { useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './Contact.css';

const Contact = () => {
  const { t } = useLanguage();
  const location = useLocation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tourMessage = urlParams.get('message');

    if (tourMessage) {
      setFormData((prev) => ({
        ...prev,
        message: tourMessage,
      }));
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus('');

    try {
      const result = await emailjs.send(
        'service_93pc078',
        'template_y5ff3zb',
        {
          user_name: formData.name,
          user_email: formData.email,
          user_phone: formData.phone,
          message: formData.message,
        },
        'W1CsBj0OV-dNpcokL'
      );

      if (result.text === 'OK') {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="contact-page">
      {/* INSTAGRAM GRADIENT DEFINITION */}
      <svg width="0" height="0">
        <defs>
          <linearGradient id="instaGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#feda75" />
            <stop offset="25%" stopColor="#fa7e1e" />
            <stop offset="50%" stopColor="#d62976" />
            <stop offset="75%" stopColor="#962fbf" />
            <stop offset="100%" stopColor="#4f5bd5" />
          </linearGradient>
        </defs>
      </svg>

      <div className="container">
        <div className="contact-title-wrapper">
          <h1>{t('contactUs')}</h1>
        </div>

        <p className="contact-subtitle">{t('getInTouch')}</p>

        {submitStatus === 'success' && (
          <div className="alert alert-success">{t('output_success')}</div>
        )}

        {submitStatus === 'error' && (
          <div className="alert alert-error">{t('output_error')}</div>
        )}

        <div className="contact-content">
          {/* LEFT SIDE — Contact Info */}
          <div className="contact-info">
            <h2>{t('contactInfo')}</h2>

            <div className="contact-item">
              <h3>📞 {t('phone')}</h3>
              <p>+995 577 430 102</p>
            </div>

            <div className="contact-item">
              <h3>✉️ {t('email')}</h3>
              <p>contact@geofansgenz.com</p>
            </div>

            <div className="contact-item">
              <h3>📍 {t('address')}</h3>
              <p>Tbilisi, Georgia</p>
            </div>

            <div className="contact-item">
              <h3>🕒 {t('businessHours')}</h3>
              <p>
                Monday - Friday: 9:00 - 18:00 <br />
                Saturday: 10:00 - 16:00 <br />
                Sunday: Closed
              </p>
            </div>

            {/* SOCIAL LINKS */}
            <div className="contact-item social-links">
              <h3>🌐 {t('followUs')}</h3>

              {/* Instagram */}
              <a
                href="https://www.instagram.com/fgtravelclub"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link icon-instagram"
              >
                <svg viewBox="0 0 24 24" className="icon">
                  <path d="M7 2C4.24 2 2 4.24 2 7v10c0 2.76 2.24 5 
                    5 5h10c2.76 0 5-2.24 5-5V7c0-2.76-2.24-5-5-5H7zm10 
                    2a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H7a3 3 0 0 
                    1-3-3V7a3 3 0 0 1 3-3h10zm-5 3a5 5 0 1 0 0 
                    10 5 5 0 0 0 0-10zm0 2a3 3 0 1 1 0 6 3 3 
                    0 0 1 0-6zm4.5-.25a1.25 1.25 0 1 1 0-2.5 
                    1.25 1.25 0 0 1 0 2.5z" />
                </svg>
                Instagram
              </a>

              {/* Facebook */}
              <a
                href="https://www.facebook.com/fgtravelclub"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link icon-facebook"
              >
                <svg viewBox="0 0 24 24" className="icon">
                  <path d="M22 12a10 10 0 1 0-11.5 9.9v-7h-2v-3h2v-2.3c0-2 
                    1.2-3.1 3-3.1 .9 0 1.8.1 2 .1v2.3h-1.1c-1 
                    0-1.3.6-1.3 1.2V12h2.6l-.4 3h-2.2v7A10 
                    10 0 0 0 22 12" />
                </svg>
                Facebook
              </a>
            </div>
          </div>

          {/* RIGHT SIDE — Contact Form */}
          <div className="contact-form">
            <h2>{t('sendMessage')}</h2>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">{t('fullName')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  placeholder="John Doe"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">{t('emailAddress')} *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  placeholder="example@mail.com"
                />
              </div>

              <div className="form-group">
                <label>{t('phone')} *</label>
                <PhoneInput
                  country={'ge'}
                  value={formData.phone}
                  enableSearch={true}
                  onChange={(value) =>
                    setFormData({ ...formData, phone: value })
                  }
                  inputProps={{
                    name: 'phone',
                    required: true,
                  }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">{t('message')} *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  placeholder="Your message..."
                ></textarea>
              </div>

              <button
                type="submit"
                className={`submit-button ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? '⏳ Sending...' : t('sendMessageBtn')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;